import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import { Button, Field } from "../components/ui";
import { useAuth } from "../auth";
import { apiGet, apiSend, API } from "../api";
import { C, R, S } from "../theme";

function b64ToBlob(b64, mime) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime || "application/octet-stream" });
}

const STEPS = ["Rides", "Documents", "Review"];

export default function SharePack() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [attractions, setAttractions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [rideIds, setRideIds] = useState([]);
  const [docIds, setDocIds] = useState([]);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    Promise.all([apiGet("/attractions").catch(() => []), apiGet("/documents").catch(() => [])]).then(([a, d]) => {
      setAttractions(a); setDocs(d);
    });
  }, []);

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const rideDocs = useMemo(() => docs.filter((d) => rideIds.includes(d.attraction_id)), [docs, rideIds]);
  const selectedDocs = useMemo(() => docs.filter((d) => docIds.includes(d.id)), [docs, docIds]);
  const selectedRides = useMemo(() => attractions.filter((a) => rideIds.includes(a.id)), [attractions, rideIds]);
  const company = user?.company_name || "SafeBook";
  const subject = `Compliance Pack — ${company}`;

  const buildBody = (links) => {
    let b = "";
    const emails = recipients.split(/[,\s]+/).filter(Boolean);
    if (emails.length) b += `To: ${emails.join(", ")}\n\n`;
    b += message ? `${message}\n\n` : "";
    b += `${company} has shared the following compliance paperwork:\n\n`;
    b += selectedDocs.map((d) => `• ${d.attraction_name} — ${d.name} (${d.category})`).join("\n");
    if (links && links.length) {
      b += `\n\nDownload links (valid 7 days):\n` + links.map((l) => `${l.attraction_name} — ${l.name}: ${l.url || API + l.path}`).join("\n");
    }
    b += `\n\nSent via SafeBook.`;
    return b;
  };

  const nativeShare = async () => {
    setBusy("share"); setMsg("");
    try {
      const files = [];
      for (const id of docIds) {
        const d = await apiGet(`/documents/${id}`);
        files.push(new File([b64ToBlob(d.content, d.mime)], `${d.attraction_name} - ${d.name}`.replace(/[^\w .-]/g, "_"), { type: d.mime || "application/octet-stream" }));
      }
      if (navigator.canShare && navigator.canShare({ files })) {
        await navigator.share({ files, title: subject });
        setMsg("✅ Shared");
      } else {
        await emailFallback();
      }
    } catch (e) {
      if (e?.name !== "AbortError") setMsg("❌ " + (e?.message || "Share failed"));
    } finally { setBusy(""); }
  };

  const emailFallback = async () => {
    setBusy("mail"); setMsg("");
    try {
      const { links } = await apiSend("/documents/share-links", "POST", { document_ids: docIds, recipients: recipients.split(/[,\s]+/).filter(Boolean), public_base: API });
      const body = buildBody(links);
      window.location.href = `mailto:${encodeURIComponent(recipients)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      setMsg("✅ Opening your email app with download links…");
    } catch (e) { setMsg("❌ " + (e?.message || "Could not prepare email")); } finally { setBusy(""); }
  };

  const shareLinks = async () => {
    setBusy("sms"); setMsg("");
    try {
      const { links } = await apiSend("/documents/share-links", "POST", { document_ids: docIds, recipients: recipients.split(/[,\s]+/).filter(Boolean), public_base: API });
      const text = buildBody(links);
      if (navigator.share) { await navigator.share({ title: subject, text }); setMsg("✅ Shared"); }
      else { window.location.href = `sms:?&body=${encodeURIComponent(text)}`; setMsg("✅ Opening your messaging app…"); }
    } catch (e) { if (e?.name !== "AbortError") setMsg("❌ " + (e?.message || "Share failed")); } finally { setBusy(""); }
  };

  const serverEmail = async () => {
    setMsg("");
    const emails = recipients.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
    if (!emails.length) { setMsg("Add at least one recipient email"); return; }
    setBusy("server");
    try { const r = await apiSend("/compliance-pack/email", "POST", { document_ids: docIds, recipients: emails, message: message || null, public_base: API }); setMsg(`✅ ${r.delivery === "links" ? "Emailed with secure download links" : "Emailed with attachments"} to ${r.sent_to.join(", ")}`); }
    catch (e) { setMsg("❌ " + (e?.message || "Failed to send")); } finally { setBusy(""); }
  };

  const checkRow = (active, title, sub, onClick, testid) => (
    <button key={testid} data-testid={testid} onClick={onClick} className="pressable" style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", background: C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${active ? C.amber : C.border}`, textAlign: "left" }}>
      <Icon name={active ? "checkmark-circle" : "cube-outline"} size={22} color={active ? C.amber : C.muted} />
      <div style={{ flex: 1 }}><div style={{ color: C.text, fontWeight: 700 }}>{title}</div><div style={{ color: C.muted, fontSize: 12 }}>{sub}</div></div>
    </button>
  );

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg, paddingBottom: S.sm }}>
        <button data-testid="pack-back" className="pressable" onClick={() => (step > 1 ? setStep(step - 1) : nav(-1))}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Send Paperwork</div>
      </div>
      <div style={{ display: "flex", gap: S.sm, padding: `0 ${S.lg}px ${S.md}px` }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ height: 6, borderRadius: 3, background: step >= i + 1 ? C.amber : C.card2 }} />
            <div style={{ color: step >= i + 1 ? C.amber : C.muted, fontSize: 11, fontWeight: 800, marginTop: 4 }}>{i + 1}. {s}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: S.lg, paddingTop: 0 }}>
        {step === 1 && (
          <>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" }}>Select rides / attractions ({rideIds.length})</div>
            {attractions.length === 0 ? <div style={{ color: C.muted }}>No attractions yet.</div> : attractions.map((a) => checkRow(rideIds.includes(a.id), a.name, `${a.category === "inflatable" ? "Inflatable" : "Ride"}`, () => toggle(rideIds, setRideIds, a.id), `pack-ride-${a.id}`))}
            <Button title="Next: Documents" testID="pack-next-1" onPress={() => setStep(2)} disabled={rideIds.length === 0} style={{ marginTop: S.md }} />
          </>
        )}
        {step === 2 && (
          <>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" }}>Select documents ({docIds.length})</div>
            {rideDocs.length === 0 ? <div style={{ color: C.muted, marginBottom: S.md }}>No documents for the selected rides. Upload them in each attraction's Document Vault.</div> : rideDocs.map((d) => checkRow(docIds.includes(d.id), d.name, `${d.attraction_name} · ${d.category}`, () => toggle(docIds, setDocIds, d.id), `pack-doc-${d.id}`))}
            <Button title="Next: Review" testID="pack-next-2" onPress={() => setStep(3)} disabled={docIds.length === 0} style={{ marginTop: S.md }} />
          </>
        )}
        {step === 3 && (
          <>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" }}>Review & Dispatch</div>
            <div data-testid="pack-summary" style={{ background: C.card, borderRadius: R.md, padding: S.md, border: `1px solid ${C.border}`, marginBottom: S.md }}>
              <div style={{ color: C.amber, fontWeight: 800, fontSize: 13 }}>{selectedRides.length} ride(s)</div>
              <div style={{ color: C.sub, fontSize: 13, marginBottom: S.sm }}>{selectedRides.map((a) => a.name).join(", ")}</div>
              <div style={{ color: C.amber, fontWeight: 800, fontSize: 13 }}>{selectedDocs.length} document(s)</div>
              {selectedDocs.map((d) => <div key={d.id} style={{ color: C.sub, fontSize: 13 }}>• {d.attraction_name} — {d.name}</div>)}
            </div>
            <Field label="Recipient email(s)" testID="pack-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="council@example.com, inspector@example.com" />
            <Field label="Message (optional)" testID="pack-message" multiline value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A short note…" />
            {!!msg && <div data-testid="pack-status" style={{ color: msg.startsWith("✅") ? C.green : C.red, fontWeight: 700, marginBottom: S.sm }}>{msg}</div>}
            <Button title="Send via Email (attach PDFs)" testID="pack-share" icon={<Icon name="mail" size={18} color={C.black} />} onPress={nativeShare} loading={busy === "share"} />
            <div style={{ color: C.muted, fontSize: 12, textAlign: "center", margin: `2px 0 ${S.sm}px` }}>Opens your mail / share menu with the PDFs attached</div>
            <Button title="Email with download links instead" testID="pack-mail" variant="dark" onPress={emailFallback} loading={busy === "mail"} />
            <div style={{ height: S.sm }} />
            <Button title="Share via Text / SMS / Apps" testID="pack-sms" variant="dark" icon={<Icon name="chatbubble-ellipses" size={18} color={C.text} />} onPress={shareLinks} loading={busy === "sms"} />
            <div style={{ color: C.muted, fontSize: 12, textAlign: "center", margin: `2px 0 ${S.sm}px` }}>(will send a downloadable link)</div>
            <Button title="Email via SafeBook (server)" testID="pack-server" variant="green" onPress={serverEmail} loading={busy === "server"} />
          </>
        )}
        <div style={{ height: S.xxl }} />
      </div>
    </div>
  );
}
